import { useQuery } from '@apollo/client'
import { useCallback } from 'react'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { LinkPlatformInput, PromocodeStatusEnum, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import {
  findAllPromoCodes,
  findAllPromoCodesVariables,
  findAllPromoCodes_findAllPromocode_items,
} from '__generatedTypes__/findAllPromoCodes'
import { GET_LIST_PROMO_CODES } from 'queries/promoCode'

export type PromoCode = findAllPromoCodes_findAllPromocode_items

export type PromoCodeFilters = {
  status?: PromocodeStatusEnum
  platform?: LinkPlatformInput
}

export function castParams({
  page,
  take,
  search,
  order,
  filters,
}: Params<PromoCodeFilters>): findAllPromoCodesVariables {
  const where: findAllPromoCodesVariables['query']['where'] = {}

  if (filters.status) {
    where.status = filters.status
  }

  if (filters.platform) {
    where.platform = filters.platform
  }

  return {
    query: {
      skip: (page - 1) * take,
      take,
      search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
      order: order?.field ? { [order.field]: order.direction } : undefined,
      where,
    },
  }
}

export function usePromoCodes() {
  return useQueryWithParams<
    findAllPromoCodes,
    findAllPromoCodesVariables,
    'findAllPromocode',
    'items',
    'count',
    PromoCodeFilters
  >(GET_LIST_PROMO_CODES, 'findAllPromocode', 'items', 'count', castParams, undefined, {
    fetchPolicy: 'network-only',
  })
}

export function usePromoCodesCount(platformId: string) {
  const allPromoCodesState = useQuery<findAllPromoCodes, findAllPromoCodesVariables>(GET_LIST_PROMO_CODES, {
    variables: {
      query: {
        take: 1,
        where: {
          platform: {
            id: platformId,
          },
        },
      },
    },
    fetchPolicy: 'network-only',
  })
  const activePromoCodesState = useQuery<findAllPromoCodes, findAllPromoCodesVariables>(GET_LIST_PROMO_CODES, {
    variables: {
      query: {
        where: {
          status: PromocodeStatusEnum.Active,
          platform: {
            id: platformId,
          },
        },
        take: 1,
      },
    },
    fetchPolicy: 'network-only',
  })

  const refetch = useCallback(() => {
    allPromoCodesState.refetch()
    activePromoCodesState.refetch()
  }, [])

  const allPromoCodesCount = allPromoCodesState.data?.findAllPromocode.count ?? 0
  const activePromoCodesCount = activePromoCodesState.data?.findAllPromocode.count ?? 0

  return {
    all: allPromoCodesCount,
    active: activePromoCodesCount,
    expired: allPromoCodesCount - activePromoCodesCount,
    refetch,
  }
}
